import { BarChart3, Target, TrendingDown, TrendingUp } from "lucide-react";
import React from "react";
import { formatNumberFixed, formatWeight as formatWeightValue } from "../../../utils/numberUtils";
import { DisplayPortfolioData, DisplayPortfolioOption, DisplayPortfolioStock } from "./types";

interface PortfolioCompositionProps {
  portfolio: DisplayPortfolioData;
  isDarkMode: boolean;
}

const formatWeight = (weight: number | string | undefined): string => {
  if (weight === undefined || weight === null) return "0.00%";
  const numWeight = typeof weight === "string" ? parseFloat(weight) : weight;
  if (!Number.isFinite(numWeight)) return "0.00%";
  return `${formatWeightValue(numWeight)}%`;
};

const formatOptionExpiration = (expiration: string): string => {
  if (!expiration) return "N/A";

  try {
    let datePart = expiration;
    if (expiration.includes(" ")) {
      datePart = expiration.split(" ")[0];
    }

    if (datePart.length === 8 && /^\d{8}$/.test(datePart)) {
      const year = datePart.substring(0, 4);
      const month = datePart.substring(4, 6);
      const day = datePart.substring(6, 8);
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }

    const date = new Date(expiration);
    if (isNaN(date.getTime())) {
      return expiration;
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return expiration;
  }
};

const getPositionIcon = (positionType: string) => {
  return positionType === "LONG" ? (
    <TrendingUp className="w-4 h-4 text-green-600" />
  ) : (
    <TrendingDown className="w-4 h-4 text-red-600" />
  );
};

const getPositionColor = (positionType: string): string => {
  return positionType === "LONG"
    ? "text-green-800 bg-green-100 border-green-200"
    : "text-red-800 bg-red-100 border-red-200";
};

// Stocks table component
const StocksTable: React.FC<{
  stocks: DisplayPortfolioStock[];
  isDarkMode: boolean;
  title?: string;
  description?: string;
}> = ({ stocks, isDarkMode, title = "Individual Stocks", description }) => {
  if (!stocks || stocks.length === 0) return null;

  return (
    <div
      className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl p-6 shadow-xl border ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      <div className="flex items-center mb-4">
        <BarChart3 className={`w-5 h-5 mr-2 ${isDarkMode ? "text-teal-400" : "text-teal-600"}`} />
        <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
          {title} ({stocks.length})
        </h2>
      </div>
      {description && (
        <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-4`}>{description}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <th
                className={`text-left py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Symbol
              </th>
              <th
                className={`text-left py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Name
              </th>
              <th
                className={`text-right py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Weight
              </th>
              <th
                className={`text-center py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Position
              </th>
            </tr>
          </thead>
          <tbody>
            {stocks.map((stock, index) => (
              <tr key={index} className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
                <td className={`py-3 px-4 font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  {stock.ticker || stock.conid}
                </td>
                <td className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {stock.name || "-"}
                </td>
                <td className={`py-3 px-4 text-right ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {formatWeight(stock.weight ?? stock.weightage)}
                </td>
                <td className="py-3 px-4 text-center">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      stock.positionType === "LONG" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}
                  >
                    {getPositionIcon(stock.positionType)}
                    <span className="ml-1">{stock.positionType}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Baskets section component
const BasketsSection: React.FC<{
  portfolio: DisplayPortfolioData;
  isDarkMode: boolean;
}> = ({ portfolio, isDarkMode }) => {
  if (!portfolio.portfolioBaskets || portfolio.portfolioBaskets.length === 0) return null;

  return (
    <div
      className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl p-6 shadow-xl border ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      <div className="flex items-center mb-4">
        <Target className={`w-5 h-5 mr-2 ${isDarkMode ? "text-teal-400" : "text-teal-600"}`} />
        <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
          Baskets ({portfolio.portfolioBaskets.length})
        </h2>
      </div>
      <div className="space-y-6">
        {portfolio.portfolioBaskets.map((portfolioBasket, index) => {
          const basket = portfolioBasket.basket || portfolioBasket.baskets;
          const basketStocks = basket?.basketStocks || [];

          return (
            <div
              key={index}
              className={`p-4 rounded-lg border ${
                isDarkMode ? "border-gray-700 bg-gray-750" : "border-gray-200 bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  {basket?.name || `Basket ${index + 1}`}
                </h3>
                <span
                  className={`px-4 py-2 rounded-xl text-sm font-bold ${
                    isDarkMode ? "bg-teal-900/50 text-teal-300" : "bg-teal-100 text-teal-800 border border-teal-200"
                  }`}
                >
                  {formatWeight(portfolioBasket.weightage)}
                </span>
              </div>
              {basket?.description && (
                <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"} mb-3`}>
                  {basket.description}
                </p>
              )}

              {basketStocks.length > 0 ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className={`border-b ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}>
                        <th
                          className={`text-left py-2 px-3 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          } font-medium text-sm`}
                        >
                          Symbol
                        </th>
                        <th
                          className={`text-left py-2 px-3 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          } font-medium text-sm`}
                        >
                          Name
                        </th>
                        <th
                          className={`text-right py-2 px-3 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          } font-medium text-sm`}
                        >
                          Weight
                        </th>
                        <th
                          className={`text-center py-2 px-3 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          } font-medium text-sm`}
                        >
                          Position
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {basketStocks.map((stock, stockIndex) => (
                        <tr
                          key={stockIndex}
                          className={`border-b ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}
                        >
                          <td
                            className={`py-2 px-3 font-medium text-sm ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {stock.ticker || stock.conid}
                          </td>
                          <td className={`py-2 px-3 text-sm ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                            {stock.name || "-"}
                          </td>
                          <td
                            className={`py-2 px-3 text-right text-sm ${
                              isDarkMode ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            {formatWeight(stock.weight)}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                stock.positionType === "LONG"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              }`}
                            >
                              {stock.positionType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div
                    className={`w-12 h-12 rounded-full ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-100"
                    } flex items-center justify-center mx-auto mb-3`}
                  >
                    <BarChart3 className={`w-6 h-6 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`} />
                  </div>
                  <p className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>
                    No holdings in this basket
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Options table component
const OptionsTable: React.FC<{
  options: DisplayPortfolioOption[];
  isDarkMode: boolean;
}> = ({ options, isDarkMode }) => {
  if (!options || options.length === 0) return null;

  const hasPrices = options.some(option => option.last);

  return (
    <div
      className={`${isDarkMode ? "bg-gray-800" : "bg-white"} rounded-2xl p-6 shadow-xl border ${
        isDarkMode ? "border-gray-700" : "border-gray-200"
      }`}
    >
      <div className="flex items-center mb-4">
        <Target className={`w-5 h-5 mr-2 ${isDarkMode ? "text-orange-400" : "text-orange-600"}`} />
        <h2 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
          Options ({options.length})
        </h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
              <th
                className={`text-left py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Symbol
              </th>
              <th
                className={`text-left py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Contract
              </th>
              <th
                className={`text-right py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Weight
              </th>
              <th
                className={`text-center py-3 px-4 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                } font-medium text-sm`}
              >
                Position
              </th>
              {hasPrices && (
                <th
                  className={`text-right py-3 px-4 ${
                    isDarkMode ? "text-gray-400" : "text-gray-600"
                  } font-medium text-sm`}
                >
                  Last Price
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {options.map((option, index) => (
              <tr key={index} className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-100"}`}>
                <td className={`py-3 px-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                  <div className="font-medium">{option.symbol || option.ticker || `ConID: ${option.conId}`}</div>
                  {option.name && !option.name.includes("Unknown") && (
                    <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"} mt-1`}>
                      {option.name}
                    </div>
                  )}
                </td>
                <td className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {option.expiration && option.strike && option.right ? (
                    <div>
                      <div className="font-medium">
                        {option.right === "C" ? "Call" : "Put"} ${option.strike}
                      </div>
                      <div className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                        {formatOptionExpiration(option.expiration)}
                      </div>
                    </div>
                  ) : (
                    <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>N/A</span>
                  )}
                </td>
                <td className={`py-3 px-4 text-right ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {formatWeight(option.weightage ?? option.weight)}
                </td>
                <td className="py-3 px-4 text-center">
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      option.positionType === "LONG" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                    }`}
                  >
                    {getPositionIcon(option.positionType)}
                    <span className="ml-1">{option.positionType}</span>
                  </span>
                </td>
                {hasPrices && (
                  <td className={`py-3 px-4 text-right ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                    {option.last ? (
                      <span className="font-medium">
                        {option.currency || "USD"} {formatNumberFixed(option.last)}
                      </span>
                    ) : (
                      <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>N/A</span>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PortfolioComposition: React.FC<PortfolioCompositionProps> = ({ portfolio, isDarkMode }) => {
  return (
    <div className="space-y-6">
      {/* Individual Stocks */}
      <StocksTable
        stocks={portfolio.portfolioStocks}
        isDarkMode={isDarkMode}
        title="Individual Stocks"
        description="Individual stocks held directly in the portfolio"
      />

      {/* Baskets */}
      <BasketsSection portfolio={portfolio} isDarkMode={isDarkMode} />

      {/* Options */}
      <OptionsTable options={portfolio.portfolioOptions} isDarkMode={isDarkMode} />
    </div>
  );
};

export default PortfolioComposition;

